<template>
  <section class="ltc-rep">
    <div class="ltc-rep__head">
      <h3 class="ltc-rep__title">급여계약내용 등록 모달</h3>
      <p class="ltc-rep__guide">년월은 요청한 {{ yearMonthLabel }}입니다. 체크는 있음·없음·미확인으로 나누고, 글자 칸은 값이 없으면 없음이라고 적습니다. 요소를 누르면 id가 위에 남습니다.</p>
    </div>
    <p class="ltc-rep__picked">
      <template v-if="picked">
        선택한 요소 <strong>{{ picked.id }}</strong>
        <span v-if="picked.label"> · {{ picked.label }}</span>
        <span v-if="copied"> · 복사됨</span>
      </template>
      <template v-else>아직 고른 요소가 없습니다.</template>
    </p>

    <div class="ltc-rep__scroll">
      <div class="ltc-rep__stage">
        <div class="ltc-rep__winbar">급여계약내용 등록(재가)[npia107p01]</div>

        <button
          v-for="el in controls"
          :key="el.id + el.top + el.left"
          type="button"
          class="ltc-rep__el"
          :class="['is-' + el.kind, { 'is-on': picked?.id === el.id }]"
          :style="box(el)"
          :title="el.id"
          @click="choose(el.id, el.label || el.text || '')"
        >
          <span class="ltc-rep__el-text">{{ controlText(el) }}</span>
        </button>

        <div class="ltc-rep__grid" :style="gridBox" title="grd_paymtDypr">
          <table class="ltc-rep__table">
            <thead>
              <tr>
                <th
                  v-for="col in scheduleCols"
                  :key="col.id"
                  :class="dayClass(col.week)"
                  :style="{ width: col.width + 'px' }"
                  :title="col.id"
                  @click="choose(col.id, col.label)"
                >{{ col.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in scheduleRowCount" :key="row">
                <td
                  v-for="col in scheduleCols"
                  :key="col.id + row"
                  :title="cellTitle(row - 1, col)"
                  @click="choose(bodyId(row - 1, col.col), col.label)"
                >
                  <span
                    v-if="col.check"
                    class="ltc-rep__box"
                    :class="'is-' + checkState(row - 1, col)"
                  />
                  <span v-else class="ltc-rep__val">{{ cellText(row - 1, col) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="ltc-rep__limit" :style="limitBox" title="grd_limit" @click="choose('grd_limit', '월 한도')">
          <span v-for="item in limitItems" :key="item.label">{{ item.label }} {{ item.value }}</span>
        </div>

        <div class="ltc-rep__grid is-cancel" :style="cancelBox" title="grd_paymtCnclctr">
          <table class="ltc-rep__table">
            <thead>
              <tr>
                <th class="is-name" title="grd_paymtCnclctr 일자" @click="choose('grd_paymtCnclctr', '일자')">일자</th>
                <th
                  v-for="day in days"
                  :key="'c' + day.day"
                  :class="dayClass(day.week)"
                  :title="cancelHeadId(day.day)"
                  @click="choose(cancelHeadId(day.day), day.day + '일')"
                >{{ day.day }}<br>({{ day.week }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="band in cancelBands" :key="band.id">
                <td class="is-name" :title="band.id" @click="choose(band.id, band.label)">{{ band.label }}</td>
                <td
                  v-for="day in days"
                  :key="band.id + day.day"
                  :title="band.cellId(day.day)"
                  @click="choose(band.cellId(day.day), band.label + ' ' + day.day + '일')"
                >
                  <span v-if="band.check" class="ltc-rep__box is-unknown" />
                  <span v-else class="ltc-rep__val">{{ band.value(day.day) || '없음' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

type Summary = {
  name?: string
  certNo?: string
  qualification?: string
  validTerm?: string
  contractTerm?: string
}

type Grid = {
  headers: string[]
  rows: string[][]
}

const props = defineProps<{
  yearMonth?: string
  summary?: Summary | null
  grid?: Grid | null
}>()

type Control = {
  id: string
  text: string
  label: string
  left: number
  top: number
  width: number
  height: number
  kind: 'label' | 'value' | 'input' | 'button' | 'search' | 'note' | 'section' | 'point'
}

const TITLE = 28
const MAIN = 85

const form = (id: string, text: string, label: string, left: number, top: number, width: number, height: number, kind: Control['kind']): Control => ({
  id, text, label, left, top: top + TITLE, width, height, kind,
})

const main = (id: string, text: string, label: string, left: number, top: number, width: number, height: number, kind: Control['kind']): Control => (
  form(id, text, label, left, top + MAIN, width, height, kind)
)

const controls: Control[] = [
  form('sta_saveMsg', '※ 중복 점검은 저장 및 통보시 수행됩니다.', '저장 안내', 737, 6, 267, 18, 'note'),
  form('sta_title', '(방문요양)', '급여종류', 867, 6, 194, 17, 'value'),
  form('btn_save', '저장 및 통보', '저장 및 통보', 1064, 4, 114, 22, 'button'),
  form('btn_close', '닫기', '닫기', 1182, 4, 68, 22, 'button'),
  form('Static10', '년월', '년월', 15, 42, 48, 16, 'label'),
  form('cmb_ctrYm', '', '년월 입력', 68, 39, 124, 22, 'input'),
  form('Static08', '대상자', '대상자', 212, 42, 72, 16, 'label'),
  form('cmb_obj', '', '대상자 입력', 282, 39, 350, 22, 'input'),
  form('btn_select', '조회', '조회', 637, 39, 75, 22, 'search'),
  main('Static18', '장기요양대상자', '장기요양대상자', 0, 3, 205, 26, 'section'),
  main('btn_cncl', '계약통보서 해지', '계약통보서 해지', 1015, 4, 107, 25, 'button'),
  main('btn_reCal', '월금액 재산정', '월금액 재산정', 1126, 4, 124, 25, 'button'),
  main('stc_13', '성명', '성명', 0, 32, 88, 32, 'label'),
  main('sta_fnm', '박은주', '성명', 96, 32, 69, 32, 'value'),
  main('stc_04', '장기요양인정번호', '장기요양인정번호', 174, 32, 135, 32, 'label'),
  main('sta_ltcMgmtNo', 'L2307138185', '장기요양인정번호', 316, 32, 92, 32, 'value'),
  main('stc_00', '자격', '자격', 416, 32, 51, 32, 'label'),
  main('sta_qlfType', '일반', '자격', 475, 32, 75, 32, 'value'),
  main('stc_15', '인정유효기간', '인정유효기간', 562, 32, 120, 32, 'label'),
  main('sta_rcgtValidTerm', '2025-08-17 ~ 2029-08-16', '인정유효기간', 687, 32, 182, 32, 'value'),
  main('stc_24', '계약기간', '계약기간', 880, 32, 120, 32, 'label'),
  main('sta_ctrTerm', '2026-09-01 00:00 ~ 2029-08-16 24:00', '계약기간', 1008, 32, 238, 32, 'value'),
  main('Static04', '급여일정등록·변경', '급여일정등록·변경', 0, 68, 179, 28, 'section'),
  main('sta_cnclMsg01', '※ 급여일정 종료시간이 익일인 경우는 노란색으로 표시됩니다.', '일정 안내', 148, 73, 371, 22, 'note'),
  main('stc_point', '', '로딩 표시', 668, 69, 51, 30, 'point'),
  main('btn_addRow', '입력', '입력', 671, 72, 45, 25, 'button'),
  main('btn_upd', '지난일정 수정', '지난일정 수정', 720, 72, 115, 25, 'button'),
  main('btn_upHist', '지난일정 수정이력', '지난일정 수정이력', 839, 72, 138, 25, 'button'),
  main('btn_print', '인쇄', '인쇄', 981, 72, 50, 25, 'button'),
  main('btn_printCal', '인쇄(달력)', '인쇄(달력)', 1035, 72, 87, 25, 'button'),
  main('btn_addPaymtOffrDesc', '지급 이후 통보', '지급 이후 통보', 1126, 72, 124, 25, 'button'),
  main('Static14', '계약통보서 해지', '계약통보서 해지', 0, 343, 287, 28, 'section'),
  main('sta_cnclMsg02', '※ 월 한도액 추가 산정 기준으로 급여일정을 등록하여도, 실제 서비스를 제공하지 않으면 추가 산정되지 않습니다.', '해지 안내', 124, 348, 900, 22, 'note'),
]

const WEEK = ['일', '월', '화', '수', '목', '금', '토']

const yearMonthLabel = computed(() => {
  const matched = /^(\d{4})-(\d{2})$/.exec(props.yearMonth || '2026-08')
  if (!matched) return '2026년 8월'
  return `${matched[1]}년 ${Number(matched[2])}월`
})

const days = computed(() => {
  const matched = /^(\d{4})-(\d{2})$/.exec(props.yearMonth || '2026-08')
  const year = matched ? Number(matched[1]) : 2026
  const month = matched ? Number(matched[2]) : 8
  const last = new Date(year, month, 0).getDate()
  return Array.from({ length: last }, (_, index) => ({
    day: index + 1,
    week: WEEK[new Date(year, month - 1, index + 1).getDay()],
    col: 14 + index,
  }))
})

const fixedCols: { col: number; label: string; width: number; check?: boolean }[] = [
  { col: 0, label: '삭제', width: 36 },
  { col: 1, label: '요양요원', width: 72 },
  { col: 3, label: '가족 여부', width: 44 },
  { col: 7, label: '시작 시간', width: 44 },
  { col: 8, label: '종료 시간', width: 44 },
  { col: 9, label: '제공 시간', width: 44 },
  { col: 10, label: '횟수', width: 36 },
  { col: 13, label: '전체 선택', width: 44, check: true },
]

type ScheduleCol = {
  col: number
  label: string
  width: number
  check?: boolean
  week: string
  id: string
  day?: number
}

const scheduleCols = computed<ScheduleCol[]>(() => [
  ...fixedCols.map((col) => ({
    ...col,
    id: `grd_paymtDypr.head.cell_-1_${col.col}`,
    week: '',
  })),
  ...days.value.map((day) => ({
    col: day.col,
    label: `${day.day}\n(${day.week})`,
    width: 30,
    check: true,
    week: day.week,
    day: day.day,
    id: `grd_paymtDypr.head.cell_-1_${day.col}`,
  })),
])

const limitItems = [
  { label: '이용횟수', value: '0' },
  { label: '인지활동형 횟수', value: '0' },
  { label: '월금액', value: '0 원' },
  { label: '타급여포함 월금액', value: '0 원' },
  { label: '월 한도액', value: '0 원' },
  { label: '월한도 초과금액', value: '0 원' },
  { label: '승인금액', value: '0 원' },
  { label: '월한도 미포함금액', value: '0 원' },
]

const cancelBands = [
  {
    id: 'grd_paymtCnclctr.등급',
    label: '등급',
    check: false,
    value: (day: number) => (day <= 16 ? '3' : '없음'),
    cellId: (day: number) => `grd_paymtCnclctr.body.cell_0_${day}`,
  },
  {
    id: 'grd_paymtCnclctr.자격',
    label: '자격',
    check: false,
    value: (day: number) => (day <= 16 ? '일반' : '없음'),
    cellId: (day: number) => `grd_paymtCnclctr.body.cell_0_${32 + day}`,
  },
  {
    id: 'grd_paymtCnclctr.해지여부',
    label: '해지여부',
    check: true,
    value: () => '',
    cellId: (day: number) => `grd_paymtCnclctr.body.cell_0_${64 + day}`,
  },
  {
    id: 'grd_paymtCnclctr.해지시간',
    label: '해지시간',
    check: false,
    value: () => '없음',
    cellId: (day: number) => `grd_paymtCnclctr.body.cell_0_${96 + day}`,
  },
]

const gridBox = { left: '0px', top: `${TITLE + MAIN + 103}px`, width: '1250px', height: '179px' }
const limitBox = { left: '0px', top: `${TITLE + MAIN + 295}px`, width: '1250px', height: '40px' }
const cancelBox = { left: '0px', top: `${TITLE + MAIN + 377}px`, width: '1250px', height: '150px' }

const picked = ref<{ id: string; label: string } | null>(null)
const copied = ref(false)

const box = (el: Control) => ({
  left: `${el.left}px`,
  top: `${el.top}px`,
  width: `${el.width}px`,
  height: `${el.height}px`,
})

const dayClass = (week: string) => {
  if (week === '일') return 'is-sun'
  if (week === '토') return 'is-sat'
  return ''
}

const bodyId = (row: number, col: number) => `grd_paymtDypr.body.cell_${row}_${col}`
const cancelHeadId = (day: number) => `grd_paymtCnclctr.head.cell_-1_${day}`

const controlText = (el: Control) => {
  if (el.id === 'cmb_ctrYm') return yearMonthLabel.value
  const summary = props.summary
  if (!summary) return el.text
  if (el.id === 'sta_fnm' && summary.name) return summary.name
  if (el.id === 'sta_ltcMgmtNo' && summary.certNo) return summary.certNo
  if (el.id === 'sta_qlfType' && summary.qualification) return summary.qualification
  if (el.id === 'sta_rcgtValidTerm' && summary.validTerm) return summary.validTerm
  if (el.id === 'sta_ctrTerm' && summary.contractTerm) return summary.contractTerm
  return el.text
}

const scheduleRowCount = computed(() => {
  const count = props.grid?.rows?.length || 0
  return count > 0 ? count : 4
})

const headerIndex = (col: ScheduleCol) => {
  const headers = props.grid?.headers || []
  if (col.day) {
    return headers.findIndex((header) => {
      const flat = String(header || '').replace(/\s+/g, ' ').trim()
      return new RegExp(`^${col.day}(?:\\s|\\(|$)`).test(flat)
    })
  }
  const key = col.label.replace(/\s/g, '')
  return headers.findIndex((header) => String(header || '').replace(/\s/g, '') === key)
}

const rawCell = (row: number, col: ScheduleCol) => {
  const line = props.grid?.rows?.[row]
  if (!line) return null
  const index = headerIndex(col)
  if (index < 0) return null
  return line[index] ?? ''
}

const checkState = (row: number, col: ScheduleCol) => {
  const raw = rawCell(row, col)
  if (raw == null) return 'unknown'
  const text = String(raw).replace(/\s/g, '')
  if (text === '✓' || text === '1' || text === 'Y' || text === 'y') return 'on'
  if (text === '' || text === '0') return 'off'
  return 'unknown'
}

const cellText = (row: number, col: ScheduleCol) => {
  const raw = rawCell(row, col)
  if (raw == null) return '없음'
  const text = String(raw).replace(/\s+/g, ' ').trim()
  return text || '없음'
}

const cellTitle = (row: number, col: ScheduleCol) => {
  const id = bodyId(row, col.col)
  if (!col.check) return `${id} ${cellText(row, col)}`
  const state = checkState(row, col)
  const label = state === 'on' ? '체크 있음' : state === 'off' ? '체크 없음' : '체크 미확인'
  return `${id} ${label}`
}

const choose = async (id: string, label: string) => {
  picked.value = { id, label }
  copied.value = false
  try {
    await navigator.clipboard.writeText(id)
    copied.value = true
  } catch {
    copied.value = false
  }
}
</script>
